import { useMemo } from 'react';

import { useGraphQLMutation, useGraphQLQuery } from '@betterment/graphql';

import { CreateEmployeeContributionConfigChangeDocument } from './graphql/mutation.createEmployeeContributionConfigChange.generated';
import {
  EmployersContributionConfigDocument,
  EmployersContributionConfigQuery,
  EmployersContributionConfigQueryVariables,
} from './graphql/query.employersContributionConfig.generated';
import {
  RetirementContributionRatesDocument,
  type RetirementContributionRatesQuery,
  type RetirementContributionRatesQueryVariables,
} from './graphql/query.retirementContributionRates.generated';
import { DeferralSettingType, DeferralSettings } from './types';

import {
  EmployeeContributionConfigurationChangeInput,
  EmployeeContributionRateType,
  LegalAccountTaxationType,
} from '../graphql/schema';

export type NormalizedRateRow = {
  employerName: string;
  legalAccountId: string;
  taxationType: LegalAccountTaxationType;
  value: number;
  contributionType: DeferralSettingType;
  bonusValue?: number;
  bonusContributionType?: DeferralSettingType;
  isEditable: boolean;
  notEditableReason: string | null;
};

export const useEmployersContributionConfigQuery = (employerId: string) => {
  const { data, loading, error } = useGraphQLQuery<
    EmployersContributionConfigQuery,
    EmployersContributionConfigQueryVariables
  >(EmployersContributionConfigDocument, {
    variables: { employerId },
    throwOnError: false,
  });

  return {
    error,
    loading,
    employers: data?.employers,
    employee: data?.employee,
    fourOhOneKContributionsThisYear: data?.fourOhOneKContributions?.thisYear,
  };
};

type PayrollContributionConfig = RetirementContributionRatesQuery['payrollContributionConfigs'][number];

type ManagedSubAccountConfig = Omit<PayrollContributionConfig, 'account'> & {
  account: Extract<PayrollContributionConfig['account'], { __typename: 'ManagedSubAccount' }>;
};

const toValue = (unionValue: ManagedSubAccountConfig['value'] | ManagedSubAccountConfig['bonusValue']): number => {
  if (!unionValue) return 0;

  if (unionValue.__typename === 'PayrollContributionConfigValueAmount') {
    return unionValue.amount.cents;
  }
  if (unionValue.__typename === 'PayrollContributionConfigValuePercent') {
    return unionValue.percent ?? 0;
  }

  return 0;
};

const toContributionType = (
  unionValue: ManagedSubAccountConfig['value'] | ManagedSubAccountConfig['bonusValue']
): DeferralSettingType => {
  if (unionValue?.__typename === 'PayrollContributionConfigValueAmount') {
    return 'amount';
  }
  return 'percent';
};

export const useRetirementContributionRatesQuery = (employerId: string) => {
  const { data, loading, error } = useGraphQLQuery<
    RetirementContributionRatesQuery,
    RetirementContributionRatesQueryVariables
  >(RetirementContributionRatesDocument, { variables: { employerId }, nextFetchPolicy: 'cache-only' });
  const employeeId = data?.employee?.legacyId;
  const rates = useMemo<NormalizedRateRow[] | undefined>(() => {
    if (!data) return undefined;

    return data.payrollContributionConfigs.flatMap(config => {
      if (config.account.__typename === 'ManagedSubAccount') {
        const row = {
          employerName: config.employer.name,
          taxationType: config.account.legalAccount.taxationType,
          legalAccountId: config.account.legalAccount.id,
          value: toValue(config.value),
          contributionType: toContributionType(config.value),
          isEditable: config.isEditable,
          notEditableReason: config.notEditableReason ?? null,
        };

        if (config.bonusValue) {
          return {
            ...row,
            bonusValue: toValue(config.bonusValue),
            bonusContributionType: toContributionType(config.bonusValue),
          };
        }

        return row;
      }
      return [];
    });
  }, [data]);

  const isEditable = rates ? (rates[0]?.isEditable ?? true) : true;
  const notEditableReason = rates?.[0]?.notEditableReason ?? null;
  return { error, loading, rates, employeeId, isEditable, notEditableReason };
};

function percentToBps(percent: number): number {
  return Math.round(percent * 100);
}

function normalizeValue(value: number, type: DeferralSettingType): number {
  if (type === 'percent') {
    return percentToBps(value);
  }
  return value;
}

function deferralSettingsToConfigs(
  newRate: DeferralSettings,
  employeeId: string
): EmployeeContributionConfigurationChangeInput[] {
  const configs = [];

  const traditionalBaseType =
    newRate.base.traditional.type === 'amount'
      ? EmployeeContributionRateType.Amount
      : EmployeeContributionRateType.Percent;
  const traditionalBonusType =
    newRate.bonus?.traditional.type === 'amount'
      ? EmployeeContributionRateType.Amount
      : EmployeeContributionRateType.Percent;

  const traditionalBaseValue = normalizeValue(newRate.base.traditional.value, newRate.base.traditional.type);
  const traditionalBonusValue = newRate.bonus?.traditional
    ? normalizeValue(newRate.bonus.traditional.value, newRate.bonus.traditional.type)
    : undefined;

  const traditionalConfig = {
    employeeId,
    benefitType: '401k',
    legalAccountId: newRate.base.traditional.legalAccountId,
    baseValue: traditionalBaseValue,
    baseType: traditionalBaseType,
    bonusValue: traditionalBonusValue,
    bonusType: newRate.bonus?.traditional ? traditionalBonusType : undefined,
  };

  configs.push(traditionalConfig);

  if (newRate.base.roth) {
    const rothBaseType =
      newRate.base.roth.type === 'amount' ? EmployeeContributionRateType.Amount : EmployeeContributionRateType.Percent;
    const rothBonusType =
      newRate.bonus?.roth?.type === 'amount'
        ? EmployeeContributionRateType.Amount
        : EmployeeContributionRateType.Percent;

    const rothBaseValue = normalizeValue(newRate.base.roth.value, newRate.base.roth.type);
    const rothBonusValue = newRate.bonus?.roth
      ? normalizeValue(newRate.bonus.roth.value, newRate.bonus.roth.type)
      : undefined;

    const rothConfig = {
      employeeId,
      benefitType: '401k',
      legalAccountId: newRate.base.roth.legalAccountId,
      baseValue: rothBaseValue,
      baseType: rothBaseType,
      bonusValue: rothBonusValue,
      bonusType: newRate.bonus?.roth ? rothBonusType : undefined,
    };
    configs.push(rothConfig);
  }

  return configs;
}

export const useCreateEmployeeContributionConfigChangeMutation = () => {
  const [createEmployeeContributionConfigChange, { loading, error, data, called }] = useGraphQLMutation(
    CreateEmployeeContributionConfigChangeDocument
  );

  const submitEmployeeContributionConfigChange = async (newRate: DeferralSettings, employeeId: string) => {
    const configs = deferralSettingsToConfigs(newRate, employeeId);

    return createEmployeeContributionConfigChange({
      variables: {
        input: {
          contributionChanges: configs,
        },
      },
    });
  };
  return {
    submitEmployeeContributionConfigChange,
    loading,
    error,
    data,
    called,
  };
};
